import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import UserAvatar from "../UserAvatar";
import style from "./styles.module.scss";
import CloseIcon from "../../../../public/images/close-icon-dark.svg";
import { getPaymentIconClasses } from "app/utils/paymentUtils";
import { cls } from "app/utils";
import { StudentProgramRegistrationModel } from "app/models/scheduling/StudentProgramRegistrationModel";
import CountdownTimer from "app/components/CountdownTimer";
import RegistrantActionsMenu from "../RegistrantActionsMenu";
import useCopyToClipboard from "app/utils/hooks/useCopyToClipboard";
import { getAge } from "app/routes/Account/FamilyEdit/utils";

type UserItemProps = {
  registration: StudentProgramRegistrationModel;
  showEmail?: boolean;
  showPaymentMethod?: boolean;
  allowRemove?: boolean;
  closeStyle?: string;
  itemStyle?: string;
  height?: number;
  width?: number;
  onClose?: (registration: StudentProgramRegistrationModel) => void;
  onExpired?: (registrationId: number) => void;
  forcePending?: boolean;
  isCurrentUser?: boolean;
  waitlisted?: boolean;
  useEllipsisMenu?: boolean;
  onManagePayment?: (registration: StudentProgramRegistrationModel) => void;
  onMarkAsPaid?: (registration: StudentProgramRegistrationModel) => void;
  onMarkAsUnpaid?: (registration: StudentProgramRegistrationModel) => void;
  onMove?: (registration: StudentProgramRegistrationModel) => void;
};

export default function UserItem(props: UserItemProps) {
  const {
    registration,
    showEmail,
    showPaymentMethod = false,
    allowRemove = true,
    forcePending = false,
    closeStyle,
    itemStyle,
    height,
    width,
    onClose,
    onExpired,
    isCurrentUser = false,
    waitlisted = false,
    useEllipsisMenu = false,
    onManagePayment,
    onMarkAsPaid,
    onMarkAsUnpaid,
    onMove,
  } = props;

  const { t } = useTranslation();
  const { copyToClipboard } = useCopyToClipboard();
  const { firstName, lastName, email, profilePicture, birthdate, headExternalId } =
    registration.studentProfile;
  // CN-1323 (CFTB-3292): a managed dependent's email is a dead/temp placeholder,
  // so show their age instead — mirrors the booking summary.
  const isDependent = Boolean(headExternalId);
  const dependentAge = birthdate ? getAge(birthdate) : null;
  const invoiceUrl = registration.invoiceUrl;
  const sharePaymentUrl = registration.sharePaymentUrl;
  const showInlineInvoiceLink = !useEllipsisMenu && !!invoiceUrl;

  const handleCopyPaymentLink = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (sharePaymentUrl) {
      copyToClipboard(sharePaymentUrl);
      toast.success(t("Payment link copied"));
    }
  };

  let displayName = 'Student';
  if (firstName && lastName) {
    displayName = `${firstName} ${lastName}`;
  } else if (email) {
    displayName = email;
  }

  const close = (
    <div
      data-testid="remove"
      onClick={() => onClose?.(registration)}
      className={closeStyle}
    >
      <div className={style.image_container}>
        <img src={CloseIcon} />
      </div>
    </div>
  );

  const isPendingWithExpiry = registration.paymentStatus === "pending" && !!registration.expiresAt;
  const shouldShowClose = registration.canRemove && allowRemove && !isPendingWithExpiry;
  const isExistingRegistration = registration.id > 0;

  const allIconClasses = getPaymentIconClasses(
    registration.paymentMethod,
    style,
    [],
    waitlisted ? undefined : (forcePending ? "pending" : registration.paymentStatus),
  );

  const iconClasses = waitlisted
    ? allIconClasses.filter(
      (clsName) =>
        clsName !== style.online &&
        clsName !== style.offline &&
        clsName !== style.package
    )
    : allIconClasses;

  return (
    <div
      className={itemStyle || style.student_item}
      data-testid="studentItem"
    >
      <div className={style.student_container}>
        <div className={style.student_avatar}>
          <UserAvatar
            src={profilePicture}
            height={height || 24}
            width={width || 24}
            first_name={firstName}
            last_name={lastName}
          />
        </div>
        <div className={style.text_container}>
          <div className={style.student_name}>
            {displayName}
            {isCurrentUser && <span className={style.me_badge}>Me</span>}
          </div>
          {isDependent ? (
            dependentAge !== null && (
              <div className={style.email}>
                {t("Age {{age}}", { age: dependentAge })}
              </div>
            )
          ) : (
            showEmail && email && <div className={style.email}>{email}</div>
          )}
        </div>
      </div>
      {isPendingWithExpiry && (
        <CountdownTimer
          expiresAt={registration.expiresAt}
          onExpire={() => onExpired?.(registration.registrationId)}
        />
      )}
      {showPaymentMethod && !isPendingWithExpiry && (
        <div className={style.payment_container}>
          <i
            className={cls(
              style.payment_icon,
              ...iconClasses,
              { [style.waitlisted]: waitlisted }
            )}
          />
        </div>
      )}
      {showInlineInvoiceLink && (
        <div className={style.invoice_link_container}>
          <a
            href={invoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={style.invoice_link}
          >
            {t("View invoice")}
          </a>
          {sharePaymentUrl && sharePaymentUrl !== invoiceUrl && (
            <a
              onClick={handleCopyPaymentLink}
              className={style.copy_link}
              aria-label={t("Copy payment link")}
            >
              <i className="ico ico-copy" />
            </a>
          )}
        </div>
      )}
      {useEllipsisMenu && isExistingRegistration && shouldShowClose ? (
        <RegistrantActionsMenu
          registration={registration}
          onRemove={(reg) => onClose?.(reg)}
          onMove={onMove}
          onManagePayment={onManagePayment}
          onMarkAsPaid={onMarkAsPaid}
          onMarkAsUnpaid={onMarkAsUnpaid}
        />
      ) : shouldShowClose ? (
        close
      ) : null}
    </div>
  );
}
